/**
 * Filter Item Component
 *
 * Manages parent-child checkbox coordination for hierarchical filters.
 *
 * @component filter-item
 * @extends HTMLLIElement (customized built-in element)
 *
 * @description
 * Controls checkbox behavior in nested filter lists. Handles three types of checkboxes:
 * - **Parent checkbox**: The main filter option (e.g., "Clothing" department)
 * - **"Show All" checkbox**: Optional checkbox that unchecks all children when checked
 * - **Child checkboxes**: Nested options under the parent (e.g., "Tops", "Bottoms")
 *
 * @behavior
 * **Parent checkbox unchecked:**
 * - All child checkboxes are automatically unchecked
 * - "Show All" is automatically checked (if present)
 *
 * **Child checkbox checked:**
 * - "Show All" is automatically unchecked
 * - Parent remains in its current state
 *
 * **Child checkbox unchecked:**
 * - If no children are checked → "Show All" is automatically checked
 * - Parent remains in its current state
 *
 * **"Show All" checkbox checked:**
 * - All child checkboxes are automatically unchecked
 *
 * **All children checked:**
 * - "Show All" is automatically checked (ready for hierarchy collapsing)
 *
 * @architecture
 * DOM Structure:
 * ```html
 * <li is="filter-item" data-param-name="filter.v.option.Department">
 *   <input name="filter.v.option.Department" value="gid://123" /> <!-- Parent -->
 *   <ul>
 *     <li>
 *       <input value="gid://456" /> <!-- "Show All" (no name attribute) -->
 *     </li>
 *     <li>
 *       <input name="filter.v.option.Category" value="gid://789" /> <!-- Child 1 -->
 *     </li>
 *     <li>
 *       <input name="filter.v.option.Category" value="gid://012" /> <!-- Child 2 -->
 *     </li>
 *   </ul>
 * </li>
 * ```
 *
 * @fires filter:change - Dispatched when any checkbox state changes (bubbles to collection-filter)
 *
 * @example
 * ```liquid
 * <li is="filter-item" data-param-name="{{ param_name }}">
 *   <input
 *     type="checkbox"
 *     name="{{ param_name }}"
 *     value="{{ metaobject.id }}"
 *     {% if filter_value contains metaobject.id %}checked{% endif %}
 *   />
 *   <label>{{ metaobject.title }}</label>
 *
 *   {% if has_children %}
 *   <ul>
 *     <li>
 *       <input type="checkbox" /> <!-- Show All (no name) -->
 *       <label>Show All</label>
 *     </li>
 *     {% for child in children %}
 *     <li>
 *       <input
 *         type="checkbox"
 *         name="{{ child_param }}"
 *         value="{{ child.id }}"
 *         {% if filter_value contains child.id %}checked{% endif %}
 *       />
 *       <label>{{ child.title }}</label>
 *     </li>
 *     {% endfor %}
 *   </ul>
 *   {% endif %}
 * </li>
 * ```
 *
 * @dependencies
 * - collection-filter.js - Listens for 'filter:change' events
 */

/**
 * Helper function to find child checkboxes within a filter item.
 *
 * @param {HTMLElement} node - The filter-item list element
 * @returns {{ all: HTMLInputElement|undefined, inputs: HTMLInputElement[] }}
 * @property {HTMLInputElement|undefined} all - "Show All" checkbox (input with no name attribute)
 * @property {HTMLInputElement[]} inputs - Array of child filter checkboxes
 */
const findAllChildren = (node) => {
  const ul = node.querySelector('ul');
  if (!ul) return { all: undefined, inputs: [] };

  const checkboxes = ul.querySelectorAll(
    ':scope > li > input:not(.hidden), :scope > li > filter-item > input:not(.hidden)'
  );

  return Array.from(checkboxes).reduce(
    (obj, item) => {
      if (!item.name) {
        obj.all = item;
      } else {
        obj.inputs.push(item);
      }
      return obj;
    },
    { all: undefined, inputs: [] }
  );
};

class FilterItem extends HTMLElement {
  #childListeners = [];

  connectedCallback() {

    this.paramName = this.getAttribute('data-param-name');
    const { all, inputs: children } = findAllChildren(this);

    this.parentInput = this.querySelector(`input[name="${this.paramName}"]`);
    this.allCheckbox = all;
    this.childInputs = children;

    const determineTriggerType = (el) => {
      if (el === this.parentInput) return 'parent';
      else if (el === this.allCheckbox) return 'all';
      else return 'child';
    };

    const handleChange = (event) => {
      const trigger = event?.currentTarget ?? this.parentInput;
      const type = determineTriggerType(trigger);
      const active = trigger.checked;

      let hasActiveItems = false;
      const allChildrenSelected = typeof event === 'undefined' ? this.childInputs.every((i) => i.checked) : false;

      for (const item of this.childInputs) {
        if (allChildrenSelected && item.checked) {
          item.checked = false;
          continue;
        }

        if (((type === 'parent' && !active) || (type === 'all' && active)) && item.checked) {
          item.checked = false;
          item.dispatchEvent(new Event('change', { bubbles: true }));
          continue;
        }

        if (item.checked && !hasActiveItems) {
          hasActiveItems = true;
        }
      }

      if (typeof event !== 'undefined') {
        this.dispatchEvent(new CustomEvent('filter:change', { bubbles: true }));
      }

      if (!this.allCheckbox) {
        return;
      }

      if (((!active && type === 'parent') || hasActiveItems) && type !== 'all') {
        this.allCheckbox.checked = false;
      } else if (!hasActiveItems || allChildrenSelected || (type === 'child' && !active && !hasActiveItems)) {
        this.allCheckbox.checked = true;
      }
    };

    // Initial state
    handleChange();

    // Add event listeners
    this.parentInput.addEventListener('change', handleChange);

    this.childInputs.forEach((child) => {
      child.addEventListener('change', handleChange);
      this.#childListeners.push({ element: child, handler: handleChange });
    });

    if (this.allCheckbox) {
      this.allCheckbox.addEventListener('change', handleChange);
    }

    // Store for cleanup
    this._handleChange = handleChange;
  }

  disconnectedCallback() {
    if (this._handleChange) {
      this.parentInput?.removeEventListener('change', this._handleChange);

      this.#childListeners.forEach(({ element, handler }) => {
        element.removeEventListener('change', handler);
      });

      if (this.allCheckbox) {
        this.allCheckbox.removeEventListener('change', this._handleChange);
      }
    }
  }
}

if (!customElements.get('filter-item')) {
  customElements.define('filter-item', FilterItem);
}
